import { useCallback, useEffect, useRef } from 'react';
import { useTwilioDevice } from '@/hooks/use-twilio-device/use-twilio-device';

const SESSION_KEY = 'ai_call_bridge';

// The Conversation STI subclass on the Rails side. Two values today —
// ElevenLabsCall (modern ConvAI) and AnalyticsCall (rai voice routed to
// the Command Center queue via the command_center_handoff_enabled flag).
// The bridge service uses this to decide which TwiML to swap to and
// which recording semantics to apply.
type AiCallConversationType = 'ElevenLabsCall' | 'AnalyticsCall';

interface StoredSession {
  conversationId: number;
  conversationType: AiCallConversationType;
  agentLanguage: string;
  visitorLanguage: string;
  translate: boolean;
}

export const useAiCallBridge = (conversationId: number | undefined) => {
  const twilio = useTwilioDevice();
  const restoredRef = useRef(false);

  const connect = useCallback(
    async (
      agentLanguage: string,
      visitorLanguage: string,
      translate: boolean = false,
      conversationType: AiCallConversationType = 'ElevenLabsCall',
    ) => {
      if (!conversationId) return;
      await twilio.connect(conversationId, '', {
        mode: 'translated',
        conversation_id: String(conversationId),
        conversation_type: conversationType,
        agent_language: agentLanguage,
        visitor_language: visitorLanguage,
        translate: translate ? 'true' : 'false',
      });
      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({
          conversationId,
          conversationType,
          agentLanguage,
          visitorLanguage,
          translate,
        } satisfies StoredSession)
      );
    },
    [conversationId, twilio]
  );

  const disconnect = useCallback(() => {
    twilio.disconnect();
    sessionStorage.removeItem(SESSION_KEY);
  }, [twilio]);

  const reconnect = useCallback(async () => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return;
      const stored: StoredSession = JSON.parse(raw);
      if (stored.conversationId !== conversationId) return;
      // `conversationType` may be missing on stored sessions from before
      // the AnalyticsCall path landed; default to ElevenLabsCall to keep
      // pre-existing reconnections working.
      await connect(
        stored.agentLanguage,
        stored.visitorLanguage,
        stored.translate ?? false,
        stored.conversationType ?? 'ElevenLabsCall',
      );
    } catch {
      // sessionStorage parse failure — ignore
    }
  }, [conversationId, connect]);

  // Restore on page reload (mirrors useConferenceCall pattern)
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;

    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return;
      const stored: StoredSession = JSON.parse(raw);
      if (stored.conversationId === conversationId) {
        connect(
          stored.agentLanguage,
          stored.visitorLanguage,
          stored.translate ?? false,
          stored.conversationType ?? 'ElevenLabsCall',
        );
      }
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  return {
    status: twilio.status,
    isMuted: twilio.isMuted,
    error: twilio.error,
    connect,
    disconnect,
    reconnect,
    toggleMute: twilio.toggleMute,
  };
};
